// src/pages/chatAI/AIChat.js
import React, { useState, useRef, useEffect } from "react";

const AIChat = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

 // 🧩 Smart and Safe Link Formatter
const formatMessage = (text) => {
  if (!text) return "";

  let formatted = text.trim();

  // 🧹 1️⃣ Clean broken or partial HTML attributes like `" target="_blank"`
  formatted = formatted.replace(
    /" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">/g,
    ""
  );

  // 🧹 2️⃣ Clean any unmatched closing tags accidentally sent by AI
  formatted = formatted.replace(/<\/?a[^>]*>/g, "");

  // 🔗 3️⃣ Convert Markdown-style links [label](url)
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  formatted = formatted.replace(
    markdownLinkRegex,
    (_, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${label}</a>`
  );

  // 🌐 4️⃣ Convert plain URLs to clickable links
  const plainUrlRegex = /(https?:\/\/[^\s]+)/g;
  formatted = formatted.replace(
    plainUrlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`
  );

  return formatted;
};

  const sendMessage = () => {
    if (input.trim() === "") return;
    setMessages((prev) => [...prev, { text: input, sender: "user" }]);
    setInput("");
    setIsTyping(true);

    fetch("https://ai-chat.mandalanil93.workers.dev", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: input }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.reply) {
          setMessages((prev) => [
            ...prev,
            { text: data.reply, sender: "bot" },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              text: "Sorry, I didn’t understand that.",
              sender: "bot",
            },
          ]);
        }
      })
      .catch(() => {
        setMessages((prev) => [
          ...prev,
          {
            text: "⚠️ Server not responding, please try again later.",
            sender: "bot",
          },
        ]);
      })
      .finally(() => setIsTyping(false));
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className="fixed bottom-5 right-5 bg-green-600 hover:bg-green-700 text-white rounded-full w-16 h-16 shadow-xl flex items-center justify-center z-50 hover:scale-110 transition-transform"
        title="Chat with Support"
      >
        <img
          src="/support-girl.webp"
          alt="Support"
          className="w-12 h-12 rounded-full"
        />
      </button>

      {/* Chat Window */}
      {chatOpen && (
        <div className="fixed bottom-24 right-5 w-80 bg-white shadow-2xl rounded-2xl overflow-hidden z-50 border border-gray-200">
          <div className="bg-green-600 text-white px-4 py-3 flex justify-between items-center">
            <span className="font-semibold">
              Anil Flyash Bricks AI Assistant
            </span>
            <button
              onClick={() => setChatOpen(false)}
              className="text-white text-xl"
            >
              &times;
            </button>
          </div>

          <div className="h-80 overflow-y-auto p-4 space-y-2 bg-gray-50 flex flex-col">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`p-2 rounded-lg max-w-[80%] ${
                  msg.sender === "user"
                    ? "bg-green-100 self-end ml-auto"
                    : "bg-gray-200"
                }`}
                dangerouslySetInnerHTML={{
                  __html: formatMessage(msg.text),
                }}
              />
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="p-2 bg-gray-200 rounded-lg max-w-[80%] text-gray-600 italic animate-pulse">
                AI is typing...
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="flex border-t">
            <input
              type="text"
              className="flex-grow p-2 outline-none"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />
            <button
              onClick={sendMessage}
              className="bg-green-600 hover:bg-green-700 text-white px-4"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChat;
